import React, { useState, useRef, useEffect } from "react";
import {
  Bold,
  Italic,
  Underline,
  AArrowDown,
  AArrowUp,
  RotateCcw,
  Palette,
  Circle,
  Smile,
} from "lucide-react";
import { handleTextStyle } from "./Util";
import EmojiPicker from "emoji-picker-react";

export default function StyledText({
  markerText,
  setMarkerText,
  index,
  disabled = false,
  testId,
}) {
  const [showButtons, setShowButtons] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [savedRange, setSavedRange] = useState(null);
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const fontSize = 16;

  // Injecter le contenu initial une seule fois
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML === "") {
      editorRef.current.innerHTML = markerText;
    }
    // On ne dépend pas de markerText pour éviter de réinjecter à chaque update.
  }, []);

  const isTextSelected = () => {
    const selection = window.getSelection();
    return selection && selection.rangeCount > 0 && !selection.isCollapsed;
  };

  const getSelectionFontSize = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return null;

    const range = selection.getRangeAt(0);
    const parentElement = range.commonAncestorContainer.parentElement;
    if (!parentElement) return null;
    const computedSize = window.getComputedStyle(parentElement).fontSize;
    return parseInt(computedSize.replace("px", ""));
  };

  const handleColor = (c) => {
    if (!isTextSelected()) return;
    document.execCommand("foreColor", false, c);
  };

  const handleFontSize = (increment) => {
    if (!isTextSelected()) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    let container = range.startContainer;
    if (container.nodeType === Node.TEXT_NODE) {
      container = container.parentElement;
    }
    const computedSize = window.getComputedStyle(container).fontSize;
    const currentSize = parseInt(computedSize.replace("px", "")) || fontSize;

    const newSize = Math.max(12, Math.min(40, currentSize + increment));

    //  Créer un <span> avec la nouvelle taille
    const span = document.createElement("span");
    span.style.fontSize = `${newSize}px`;
    span.textContent = selectedText;

    //  Remplacer le contenu sélectionné
    range.deleteContents();
    range.insertNode(span);

    //  Réinitialiser la sélection sur le nouveau span
    selection.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.addRange(newRange);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      setSavedRange(sel.getRangeAt(0));
    }
  };

  const handleEmojiClick = (emojiObject) => {
    const emoji = emojiObject.emoji;
    if (!editorRef.current) return;
    // Restaurer la sélection sauvegardée
    if (savedRange) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange);
    }
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(emoji);
    range.insertNode(textNode);
    setShowEmojiPicker(false);
  };

  const handleResetStyles = () => {
    document.execCommand("removeFormat", false, null);
  };

  const handleShowButtons = () => {
    if (!editorRef.current) return;
    const hasText = editorRef.current.textContent.trim() !== "";
    if (hasText !== showButtons) setShowButtons(hasText);
  };

  const handleBlur = () => {
    if (editorRef.current) {
      setMarkerText(editorRef.current.innerHTML, index);
      saveSelection();
    }
  };

  useEffect(() => {
    // Repli au « click » et non au « mousedown » : la barre d'outils est dans
    // le flux (pas de Tailwind dans ce projet, « absolute » y est sans effet).
    // Replier dès le mousedown décalait la page sous le pointeur et le clic
    // visé était perdu (ex. case de bonne réponse d'un QCM juste après une
    // saisie). Une sélection commencée dans l'éditeur et relâchée dehors ne
    // replie pas la barre, comme avant. Écoute en phase de capture : un
    // stopPropagation() sur un clic (boutons de suppression) n'empêche pas le
    // repli.
    let pressedInside = false;
    const handleMouseDown = (e) => {
      pressedInside = !!(
        containerRef.current && containerRef.current.contains(e.target)
      );
    };
    const handleClickOutside = (e) => {
      if (pressedInside) {
        // Un seul clic concerné : le suivant (ex. case cochée au clavier,
        // sans mousedown) doit pouvoir replier la barre.
        pressedInside = false;
        return;
      }
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowButtons(false);
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {showButtons && (
        <div
          className="absolute top-[-80px] left-0 flex flex-col gap-1 bg-grey p-2 shadow-lg border rounded"
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center gap-2">
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                handleTextStyle(
                  "underline",
                  setMarkerText,
                  editorRef,
                  isTextSelected
                );
              }}
            >
              <Underline className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                handleTextStyle(
                  "bold",
                  setMarkerText,
                  editorRef,
                  isTextSelected
                );
              }}
            >
              <Bold className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                handleTextStyle(
                  "italic",
                  setMarkerText,
                  editorRef,
                  isTextSelected
                );
              }}
            >
              <Italic className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                handleFontSize(2);
              }}
            >
              <AArrowUp className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                handleFontSize(-2);
              }}
            >
              <AArrowDown className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowColorPicker(!showColorPicker);
              }}
            >
              <Palette className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowEmojiPicker(!showEmojiPicker);
              }}
            >
              <Smile className="w-5 h-5 text-dark" />
            </a>
            <a
              type="button"
              className="p-1"
              onMouseDown={(e) => {
                e.preventDefault();
                handleResetStyles();
              }}
            >
              <RotateCcw className="w-5 h-5 text-dark" />
            </a>
          </div>
          <div className="flex items-center gap-2 justify-center mt-1">
            {showColorPicker &&
              ["red", "green", "blue", "orange", "purple", "black"].map((c) => (
                <Circle
                  key={c}
                  size={24}
                  fill={c}
                  stroke="none"
                  className="cursor-pointer hover:scale-110 transition-transform"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleColor(c);
                  }}
                />
              ))}
          </div>
          <div className="flex items-center justify-center mt-1 w-full">
            {showEmojiPicker && (
              <div className="mx-auto">
                <EmojiPicker
                  onEmojiClick={handleEmojiClick}
                  width={"100%"}
                  height={"360px"}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <br />
      <div
        ref={editorRef}
        contentEditable={!disabled}
        dir="ltr"
        className={`inputModifMarker1 bg-white ${
          disabled ? "opacity-50 pointer-events-none" : ""
        }`}
        data-testid={testId ? testId : "styled-text-editor"}
        onFocus={handleShowButtons}
        onInput={handleShowButtons}
        onBlur={handleBlur}
      />
    </div>
  );
}
